import {
  Box,
  FocusLock,
  Grid as ChakraGrid,
  IconButton,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { Grid } from '@time-line/components/common';
import { Close, Hamburger, TimelineLogo } from '@time-line/components/icons';
import { useCartCount } from 'hooks/cart/useCartCount';
import { useHeader } from 'hooks/useHeader';
import { useIsCampaignPage } from 'hooks/useIsCampaignPage';
import { useSiteSpecificRoutes } from 'hooks/useSiteSpecificRoutes';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import Headroom from 'react-headroom';
import { useMeasure } from 'react-use';

import {
  MOBILE_MENU_ID,
  NAV_ROW_HEIGHT_WITH_TOGGLE,
  RESPONSIVE_BP,
} from './constants';
import { IconNav } from './IconNav';
import { MegaMenu } from './MegaMenu';
import { MobileMenu } from './MobileMenu';
import { SecondaryNav } from './SecondaryNav';
import { SiteToggle } from './SiteToggle';
import { useNav } from './useNav';

export const Header = () => {
  const cartCount = useCartCount();
  const setIsFixed = useHeader((state) => state.setIsFixed);
  const themeName = useHeader((state) => state.themeName);
  const isSearchOpen = useHeader((state) => state.isSearchOpen);
  const router = useRouter();
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const [HOME] = useSiteSpecificRoutes(['HOME']);

  const isCampaignPage = useIsCampaignPage();

  // NOTE: useState vs useDisclosure for MobileMenu state
  // In theory we could use useDisclosure instead of useState, however
  // using useDisclosure twice on the same page current results in
  // multiple rendering of components.
  // https://github.com/chakra-ui/chakra-ui/issues/5517
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [megaMenuOpenId, setMegaMenuOpenId] = useState<string | null>(null);

  const { nav, primaryNav } = useNav();
  const [headerMeasureRef, { height: headerHeight }] =
    useMeasure<HTMLDivElement>();

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--header-height',
      `${headerHeight}px`,
    );
  }, [headerHeight]);

  useEffect(() => {
    const closeMenus = () => {
      setMobileMenuOpen(false);
      setMegaMenuOpenId(null);
    };
    router.events.on('routeChangeStart', closeMenus);

    return () => router.events.off('routeChangeStart', closeMenus);
  }, [router.events]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      // The search overlay sits on top and owns Escape while it is open.
      if (event.key !== 'Escape' || isSearchOpen) return;
      setMobileMenuOpen(false);
      mobileMenuButtonRef.current?.focus();
    };
    // Capture, because a mounted Drawer stops Escape propagating on document
    // before it would ever reach a bubble-phase listener here.
    window.addEventListener('keydown', closeOnEscape, true);

    return () => window.removeEventListener('keydown', closeOnEscape, true);
  }, [isMobileMenuOpen, isSearchOpen]);

  return (
    <Box
      className="header"
      width="100%"
      zIndex="header"
      id="header"
      sx={{
        '.headroom': {
          transition: 'background-color 0.2s',
          color:
            themeName === 'dark' && !isMobileMenuOpen ? 'white' : 'gray.600',
        },
        '.headroom--scrolled': {
          bgColor: 'gray.50',
          color: 'gray.600',
          transition: 'background-color 0.2s',
        },
      }}
    >
      <Headroom
        style={{ transition: 'transform var(--header-transition)' }}
        disable={isCampaignPage && cartCount < 1}
        pin={!!megaMenuOpenId}
        onPin={() => {
          setIsFixed(true);
          document.documentElement.style.setProperty(
            '--header-offset',
            `${headerHeight}px`,
          );
        }}
        onUnpin={() => {
          setIsFixed(false);
          document.documentElement.style.setProperty('--header-offset', '0px');
        }}
      >
        <Box ref={headerMeasureRef}>
          {/* Wraps the bar as well as the panel: the close button, logo and
           * cart live in the bar, so a lock around the panel alone would
           * leave the open menu with no reachable way out. Disabled while
           * the search overlay is up, since that portal owns focus. */}
          <FocusLock
            isDisabled={!isMobileMenuOpen || isSearchOpen}
            initialFocusRef={mobileMenuButtonRef}
            restoreFocus={false}
          >
            <Grid as="header">
              <Grid.Inner
                position="relative"
                zIndex={2}
                gridColumn={{ base: 'full', [RESPONSIVE_BP]: 'main' }}
                alignItems="center"
                gridRowGap={0}
                justifyContent="space-between"
                templateColumns={{
                  base: '[main-start] 1fr 115px 1fr [main-end]',
                  [RESPONSIVE_BP]: '[main-start] 1fr 143px 1fr [main-end]',
                }}
                gridAutoRows={{
                  base: !isCampaignPage
                    ? `${NAV_ROW_HEIGHT_WITH_TOGGLE} var(--timeline-space-2xl)`
                    : 'var(--timeline-space-2xl)',
                  [RESPONSIVE_BP]: `var(--timeline-space-2xl) ${NAV_ROW_HEIGHT_WITH_TOGGLE}`,
                }}
                mt={{
                  base: !isCampaignPage ? '4xs' : 0,
                  [RESPONSIVE_BP]: 0,
                }}
                mb={{
                  base: 0,
                  [RESPONSIVE_BP]: isCampaignPage ? 0 : 'xs',
                }}
              >
                {!isCampaignPage && <SiteToggle />}

                {!isCampaignPage && (
                  <IconButton
                    ref={mobileMenuButtonRef}
                    icon={
                      isMobileMenuOpen ? (
                        <Close width="sm" />
                      ) : (
                        <Hamburger width="sm" />
                      )
                    }
                    onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
                    variant="unstyled"
                    display={{ base: 'block', [RESPONSIVE_BP]: 'none' }}
                    aria-label={isMobileMenuOpen ? 'Close Menu' : 'Open Menu'}
                    aria-expanded={isMobileMenuOpen}
                    aria-controls={MOBILE_MENU_ID}
                    justifySelf="start"
                    pl={{ base: 'sm', [RESPONSIVE_BP]: '0' }}
                  />
                )}

                <ChakraLink
                  as={NextLink}
                  href={HOME}
                  title="Home"
                  lineHeight={1}
                  gridColumn={2}
                  zIndex={1}
                >
                  <TimelineLogo
                    verticalAlign="-1px"
                    width={{ base: '115px', [RESPONSIVE_BP]: '143px' }}
                    height={{ base: '2xs', [RESPONSIVE_BP]: 'xs' }}
                  />
                </ChakraLink>

                <ChakraGrid
                  gridAutoFlow="column"
                  gap="sm"
                  justifySelf="end"
                  zIndex={1}
                  pr={{ base: 'sm', [RESPONSIVE_BP]: '0' }}
                >
                  <SecondaryNav />
                  <IconNav variant={isCampaignPage ? 'simple' : 'default'} />
                </ChakraGrid>

                {!isCampaignPage && (
                  <MegaMenu
                    megaMenuOpenId={megaMenuOpenId}
                    setMegaMenuOpenId={setMegaMenuOpenId}
                    primaryNav={primaryNav}
                    cta={nav.cta}
                  />
                )}
              </Grid.Inner>
            </Grid>
            {!isCampaignPage && (
              <MobileMenu
                isOpen={isMobileMenuOpen}
                nav={{
                  ...nav,
                  primary: primaryNav,
                }}
              />
            )}
          </FocusLock>
        </Box>
      </Headroom>
    </Box>
  );
};
